import { Button, Combobox, Input, InputBase, Loader, ScrollArea, Stack, useCombobox } from "@mantine/core";
import { useMap } from "@mantine/hooks";
import { modals } from "@mantine/modals";
import { Suspense, useState } from "react";
import { StrictUndef } from "shared/api/httpClient";
import { handleRoleAssign, OrganizationRolesRepository, UserSchema } from "shared/components/organization/users/data";
import { GroupAssignComponent } from "shared/components/organization/users/GroupRoleManage";
import { IpawsRoleManage } from "shared/components/organization/users/IpawsRoleManage";
import { TopicAssignPanel } from "shared/components/organization/users/TopicRoleManage";
import { features } from "shared/graphql/features";
import { useGraphqlMutation } from "shared/hooks/useGraphql";
import { permissionCheck, usePermissions } from "shared/stores/oidc";
import { logger } from "shared/utils/logger";
import { graphql } from "src/gql";
import { FullUserFragment, Permission, PermissionType } from "src/gql/graphql";
import { match } from "ts-pattern";
import { container } from "tsyringe";

export const RoleAssignModal = ({ userIds }: { userIds: string[] }) => {
    const { permissions } = usePermissions();

    const hasTopics = features.enabled("HAS_TOPICS");
    const hasIpaws = features.enabled("HAS_IPAWS");

    const combobox = useCombobox({
        onDropdownClose: () => combobox.resetSelectedOption(),
    });
    const [selectedRole, setSelectedRole] = useState<UserSchema["roles"][0]["role"]>();

    const loader = container.resolve(OrganizationRolesRepository).get();
    const roles = loader.useItems();

    const assignCustomerRole = useGraphqlMutation(
        graphql(`
            mutation RoleAssignAssignCustomerRole($userId: UUID!, $roleId: UUID!) {
                assignCustomerRole(params: { roleId: $roleId, userId: $userId }) {
                    user {
                        ...FullUser
                    }
                }
            }
        `),
    );

    const options = roles
        .filter((it) =>
            match(it.type)
                .with(PermissionType.Customer, () => permissionCheck(permissions, Permission.CustomerRolesManage))
                .with(PermissionType.Group, () => permissionCheck(permissions, Permission.GroupRolesManage))
                .with(PermissionType.Topic, () => hasTopics && permissionCheck(permissions, Permission.TopicRolesManage))
                .with(PermissionType.Ipaws, () => hasIpaws && permissionCheck(permissions, Permission.CustomerRolesManage))
                .exhaustive(),
        )
        .map((role) => (
            <Combobox.Option value={role.id} key={role.id}>
                {role.name}
            </Combobox.Option>
        ));

    const groupDiff = useMap<string, "add" | "remove">();
    function onClose() {
        if (groupDiff.size > 0) {
            if (!window.confirm("You have unsaved changes. Continue?")) return;
        }
        modals.closeAll();
    }

    return (
        <Stack pos="relative">
            <Combobox
                onOptionSubmit={(val) => {
                    combobox.closeDropdown();
                    setSelectedRole(roles.find((r) => r.id == val));
                }}
                store={combobox}
            >
                <Combobox.Target>
                    <InputBase
                        component="button"
                        type="button"
                        label="Role"
                        pointer
                        required
                        rightSection={<Combobox.Chevron />}
                        rightSectionPointerEvents="none"
                        onClick={() => combobox.toggleDropdown()}
                        onBlur={() => combobox.closeDropdown()}
                    >
                        {!!selectedRole ? selectedRole.name : <Input.Placeholder>Pick role</Input.Placeholder>}
                    </InputBase>
                </Combobox.Target>
                <Combobox.Dropdown>
                    <Combobox.Options>
                        <ScrollArea.Autosize type="scroll" mah={200}>
                            {options.length === 0 ? <Combobox.Empty>No available roles</Combobox.Empty> : options}
                            <loader.LoadTrigger />
                        </ScrollArea.Autosize>
                    </Combobox.Options>
                </Combobox.Dropdown>
            </Combobox>
            <Suspense fallback={<Loader />}>
                {!!selectedRole &&
                    match(selectedRole.type)
                        .with(PermissionType.Customer, () => (
                            <Button
                                w="100%"
                                onClick={() => {
                                    handleRoleAssign(
                                        userIds.map((id) =>
                                            assignCustomerRole
                                                .mutateAsync({ userId: id, roleId: selectedRole.id })
                                                .then((res) => res.assignCustomerRole.user as StrictUndef<FullUserFragment>),
                                        ),
                                    ).catch(logger.error);
                                }}
                            >
                                Save
                            </Button>
                        ))
                        .with(PermissionType.Group, () => (
                            <GroupAssignComponent
                                userIds={userIds}
                                selectedRoleId={selectedRole.id}
                                onClose={onClose}
                                groupDiff={groupDiff}
                            />
                        ))
                        .with(PermissionType.Topic, () => <TopicAssignPanel userIds={userIds} selectedRoleId={selectedRole.id} />)
                        .with(PermissionType.Ipaws, () => <IpawsRoleManage userIds={userIds} selectedRoleId={selectedRole.id} />)
                        .exhaustive()}
            </Suspense>
        </Stack>
    );
};
